import { useState } from "react";

type Notif = {
  id: string;
  title: string;
  message: string;
  type: "order" | "review" | "stock" | "system" | "promo";
  read: boolean;
  time: string;
};

const icons: Record<Notif["type"], string> = { order:"📦", review:"⭐", stock:"⚠️", system:"🔔", promo:"🎉" };
const colors: Record<Notif["type"], { bg: string; text: string }> = {
  order:  { bg:"#dbeafe", text:"#1d4ed8" },
  review: { bg:"#fef3c7", text:"#92400e" },
  stock:  { bg:"#fee2e2", text:"#991b1b" },
  system: { bg:"#f1f5f9", text:"#475569" },
  promo:  { bg:"#fce7f3", text:"#9d174d" },
};

const initialNotifs: Notif[] = [
  { id:"n1", title:"New Order Received", message:"Order #ORD-2891 placed by Priya Sharma for ₹18,999", type:"order", read:false, time:"2 min ago" },
  { id:"n2", title:"Low Stock Alert", message:"Kundan Choker Set has only 3 units remaining", type:"stock", read:false, time:"15 min ago" },
  { id:"n3", title:"New Review", message:"5-star review received on Polki Bridal Necklace", type:"review", read:false, time:"1 hour ago" },
  { id:"n4", title:"Flash Sale Starting", message:"Festive sale starts in 2 hours. 50 products scheduled.", type:"promo", read:true, time:"3 hours ago" },
  { id:"n5", title:"System Update", message:"Admin panel updated to v2.1.0. New features added.", type:"system", read:true, time:"Yesterday" },
  { id:"n6", title:"Order Delivered", message:"Order #ORD-2850 delivered successfully to Anjali Singh", type:"order", read:true, time:"Yesterday" },
];

export function NotificationsPage() {
  const [notifs, setNotifs] = useState<Notif[]>(initialNotifs);
  const [filter, setFilter] = useState<"all"|"unread">("all");

  function markRead(id: string) { setNotifs(p => p.map(n => n.id===id ? {...n,read:true} : n)); }
  function markAllRead() { setNotifs(p => p.map(n => ({...n,read:true}))); }
  function deleteNotif(id: string) { setNotifs(p => p.filter(n => n.id!==id)); }
  const [pendingDeleteId, setPendingDeleteId] = useState<string|null>(null);

  const shown = filter==="unread" ? notifs.filter(n=>!n.read) : notifs;
  const unreadCount = notifs.filter(n=>!n.read).length;

  return (
    <>
      <style>{`
        .ntf-topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; flex-wrap:wrap; gap:12px; }
        .ntf-tabs { display:flex; gap:6px; }
        .ntf-tab { padding:7px 16px; border-radius:20px; border:1.5px solid #e2e8f0; background:#fff; font-size:13px; font-weight:600; cursor:pointer; color:#64748b; transition:all .15s; }
        .ntf-tab.active { background:var(--theme); border-color:var(--theme); color:#fff; }
        .ntf-mark-all { padding:7px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; font-size:13px; font-weight:600; cursor:pointer; color:var(--theme); transition:all .15s; }
        .ntf-mark-all:hover { background:var(--theme-light); border-color:var(--theme); }
        .ntf-item { background:#fff; border-radius:14px; border:1.5px solid #f1f5f9; padding:16px 18px; margin-bottom:10px; display:flex; gap:14px; align-items:flex-start; transition:all .2s; cursor:pointer; }
        .ntf-item.unread { border-color:#e0e7ff; background:#fafbff; }
        .ntf-item:hover { box-shadow:0 4px 12px rgba(0,0,0,.08); transform:translateY(-1px); }
        .ntf-icon { width:42px; height:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
        .ntf-title { font-size:14px; font-weight:700; color:#1e293b; }
        .ntf-msg { font-size:13px; color:#64748b; margin-top:3px; line-height:1.5; }
        .ntf-time { font-size:11px; color:#94a3b8; margin-top:6px; }
        .ntf-dot { width:8px; height:8px; border-radius:50%; background:var(--theme); flex-shrink:0; margin-top:6px; }
        .ntf-actions { display:flex; gap:6px; margin-left:auto; flex-shrink:0; }
        .ntf-btn { width:30px; height:30px; border-radius:8px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; transition:all .15s; }
        .ntf-btn:hover { border-color:var(--theme); }
        .ntf-empty { text-align:center; padding:60px; color:#94a3b8; font-size:15px; }
        .ntf-unread-badge { background:var(--theme); color:#fff; border-radius:20px; padding:2px 8px; font-size:11px; font-weight:700; margin-left:6px; }
      `}</style>
      <div>
        <div className="ntf-topbar">
          <div className="ntf-tabs">
            <button className={`ntf-tab ${filter==="all"?"active":""}`} onClick={()=>setFilter("all")} type="button">
              All {notifs.length > 0 && <span>{notifs.length}</span>}
            </button>
            <button className={`ntf-tab ${filter==="unread"?"active":""}`} onClick={()=>setFilter("unread")} type="button">
              Unread {unreadCount > 0 && <span className="ntf-unread-badge">{unreadCount}</span>}
            </button>
          </div>
          {unreadCount > 0 && (
            <button className="ntf-mark-all" onClick={markAllRead} type="button">✓ Mark all as read</button>
          )}
        </div>

        {shown.length === 0 ? (
          <div className="ntf-empty">🎉 You're all caught up!</div>
        ) : shown.map(n => (
          <div key={n.id} className={`ntf-item ${!n.read?"unread":""}`} onClick={()=>markRead(n.id)}>
            <div className="ntf-icon" style={{background:colors[n.type].bg}}>{icons[n.type]}</div>
            <div className="ntf-icon-wrap ntf-flex1">
              <div className="ntf-title">{n.title}</div>
              <div className="ntf-msg">{n.message}</div>
              <div className="ntf-time">🕐 {n.time}</div>
            </div>
            {!n.read && <div className="ntf-dot"></div>}
            <div className="ntf-actions" onClick={e=>e.stopPropagation()}>
              <button className="ntf-btn" title="Delete" onClick={()=>setPendingDeleteId(n.id)} type="button">🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirm Modal */}
      {pendingDeleteId && (
        <div className="ns-del-overlay" onClick={e=>{if(e.target===e.currentTarget)setPendingDeleteId(null);}}>
          <div className="ns-del-modal" role="dialog">
            <div className="ns-del-top">
              <div className="ns-del-icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <div className="ns-del-title">Delete Notification?</div>
              <p className="ns-del-desc">This notification will be permanently removed.</p>
            </div>
            <div className="ns-del-hr" />
            <div className="ns-del-btns">
              <button type="button" className="ns-del-cancel" onClick={()=>setPendingDeleteId(null)}>Cancel</button>
              <button type="button" className="ns-del-confirm" onClick={()=>{deleteNotif(pendingDeleteId);setPendingDeleteId(null);}}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
